import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Overline, Reveal } from "./Shared";
import { scrollToId } from "@/hooks/useSmoothScroll";

const EASE = [0.16, 1, 0.3, 1];

const CampaignGrid = ({ campaigns, selected, onSelect }) => {
  const [filter, setFilter] = useState("");
  const q = filter.trim().toLowerCase();
  const visible = q
    ? campaigns.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          c.desc.toLowerCase().includes(q)
      )
    : campaigns;

  const handleSelect = (id) => {
    onSelect(id);
    scrollToId("case-review");
  };

  return (
    <section
      id="case-types"
      data-testid="campaign-section"
      className="border-b border-[#E7E2DA] py-20 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline>Chapter 02 — Active Reviews</Overline>
              <h2 className="mt-4 font-serif font-semibold text-3xl sm:text-4xl tracking-tight text-[#1A1715] leading-tight">
                Find the review that matches what happened to you.
              </h2>
              <p className="mt-5 font-news text-base text-[#44403C] leading-relaxed">
                Our intake network currently reviews the matters below. Select
                one to begin — if yours isn't listed, choose{" "}
                <em>Other Injury or Harm</em>.
              </p>
              <div className="mt-8">
                <label
                  htmlFor="campaign-filter"
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C]"
                >
                  Filter case types
                </label>
                <input
                  id="campaign-filter"
                  data-testid="campaign-filter-input"
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="e.g. asbestos, roundup, mesh…"
                  className="mt-2 w-full bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm px-4 py-3 font-sans text-sm text-[#1A1715] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#C2410C] focus:ring-1 focus:ring-[#C2410C] transition-colors"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visible.map((c, i) => {
                const isSelected = selected === c.id;
                return (
                  <motion.button
                    key={c.id}
                    type="button"
                    data-testid={`campaign-card-${c.id}`}
                    onClick={() => handleSelect(c.id)}
                    aria-pressed={isSelected}
                    className={`group relative text-left bg-[#FFFFFF] border rounded-sm p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(26,23,21,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C2410C] ${
                      isSelected
                        ? "border-[#C2410C] shadow-[0_16px_40px_rgba(194,65,12,0.10)]"
                        : "border-[#E7E2DA]"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: (i % 4) * 0.05, ease: EASE }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#78716C]">
                        {c.category}
                      </span>
                      {isSelected ? (
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#C2410C]">
                          <Check className="h-3.5 w-3.5 text-[#FBF9F5]" strokeWidth={3} />
                        </span>
                      ) : (
                        <ArrowUpRight className="h-4 w-4 text-[#A8A29E] transition-all duration-300 group-hover:text-[#C2410C] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      )}
                    </div>
                    <h3 className="mt-3 font-serif font-semibold text-xl text-[#1A1715] leading-snug">
                      {c.name}
                    </h3>
                    <p className="mt-2 font-sans text-sm text-[#78716C] leading-relaxed">
                      {c.desc}
                    </p>
                    <span className="mt-4 inline-block bg-[#FEF3C7] text-[#92400E] font-mono text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-sm">
                      {c.tag}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            {visible.length === 0 && (
              <p
                data-testid="campaign-filter-empty"
                className="mt-6 font-news italic text-[#78716C]"
              >
                No case types match “{filter}”. Try a different term, or choose
                “Other Injury or Harm”.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CampaignGrid;
